<template>
    <div class="main-container px-1 pt-4  md:px-8 ">
        <!-- Mobile Accordion -->
        <div v-if="isMobile">
            <Accordion value="0" class="card-bg shadow-md rounded-xl">
                <AccordionPanel>
                    <AccordionHeader>
                        <span class="font-semibold text-lg pl-5">
                            Date Filters{{ isAdmin ? ' / Export' : '' }}
                        </span>
                    </AccordionHeader>
                    <AccordionContent>
                        <DateFilters v-model:dates="dates" v-model:selectedMonthIndex="selectedMonthIndex"
                            v-model:selectedDatePreset="selectedDatePreset" :months="months" :datePresets="datePresets"
                            :isAdmin="isAdmin" :isExporting="isExporting" :canExport="canExport" :isMobile="isMobile"
                            :publisherSplit="publisherSplit" @monthSelect="handleMonthSelect" @datePresetChange="handleDatePresetChange" />
                    </AccordionContent>
                </AccordionPanel>
            </Accordion>
            <!-- here show the month selectors, since its mobile they should always be visible -->
            <!-- <div class="flex flex-wrap gap-1 justify-center mt-3">
                <Button v-for="(month, index) in months" :key="index" :label="month.label"
                    @click="selectedMonth(index, month.start, month.end)"
                    :severity="selectedMonthIndex === index ? 'primary' : 'secondary'" class="h-7" />
            </div> -->
            <div class="flex justify-center items-center gap-4 mt-3">
                <Button icon="pi pi-chevron-left" @click="navigateMonth(-1)" :disabled="!canNavigateBack"
                    severity="secondary" outlined class="p-button-text h-7" />
                <Button :severity="currentMonthLabel ? 'primary' : 'secondary'" class="min-w-[100px] font-semibold h-7">
                    {{ currentMonthLabel || 'Custom Range' }}
                </Button>
                <Button icon="pi pi-chevron-right" @click="navigateMonth(1)" :disabled="!canNavigateForward"
                    severity="secondary" outlined class="p-button-text h-7" />
            </div>
        </div>

        <!-- Desktop View -->
        <div v-else class="card-bg shadow-md rounded-xl p-4">
            <DateFilters v-model:dates="dates" v-model:selectedMonthIndex="selectedMonthIndex"
                v-model:selectedDatePreset="selectedDatePreset" :months="months" :datePresets="datePresets"
                :isAdmin="isAdmin" :isExporting="isExporting" :canExport="canExport" :isMobile="isMobile"
                :publisherSplit="publisherSplit" v-model:publisherSplit="publisherSplit" @monthSelect="handleMonthSelect" @datePresetChange="handleDatePresetChange" />
        </div>
        <Skeleton v-if="loading" class="card-bg mt-10" width="100%" height="60vh"></Skeleton>
        <div v-else-if="isFinance">
            <FinanceAdvertiserView :advertiserData="advertiserEarnings" :loading="loading"
                :selectedMonth="selectedMonthDate" :isMobile="isMobile" :publisherSplit="publisherSplit" 
                :from="dates[0]" :to="dates[1]" />
        </div>
        <DataTable v-else :selectionMode="isMobile || isAdmin ? null : 'single'" :value="advertiserEarnings"
            dataKey="advertiserId" scrollable :scrollHeight="'72svh'" :loading="loading"
            class="rounded-xl shadow-sm md:col-span-1 mt-5" fluid v-model:selection="selectedRow" 
            v-model:expandedRows="expandedRows" @selection-change="onSelectionChange" sortField="customerTotalRevenue"
            :sortOrder="-1">
            <Column v-if="!isMobile || isAdmin" :expander="true" :headerStyle="{
                ...headerStyles,
            }" :bodyStyle="{
                paddingLeft: '0',
                paddingRight: '0'
            }" frozen alignFrozen="left" />
            <Column field="advertiserName" header="Adv" footer="Totals" :headerStyle="headerStyles" :bodyStyle="{
                paddingLeft: '2',
                paddingRight: '0'
            }" frozen alignFrozen="left">
            </Column>
            <Column field="clicks" header="C's" :footer="totalClicks" :headerStyle="headerStyles">

            </Column>
            <Column field="uniqueClicks" header="UC's" :footer="totalUniqueClicks" :headerStyle="headerStyles">
            </Column>
            <Column field="signups" header="SU's" :footer="totalSignups" :headerStyle="headerStyles">

            </Column>
            <Column field="FTD" header="FTDs" :footer="totalFTD" :headerStyle="headerStyles">

            </Column>
            <Column field="CPACount" header="CPAs" sortable :footer="totalCPAs" :headerStyle="headerStyles">

            </Column>
            <Column field="deposits" header="Deposits" :footer="totalDeposits" :headerStyle="headerStyles">

            </Column>
            <Column field="depositValue" header="D Val" :footer="totalDepositValue" :headerStyle="headerStyles">
                <template #body="slotProps">
                    <div>
                        <!-- Call the formatCurrency function, now it always formats in Euro -->
                        <p>{{ formatCurrency(slotProps.data.depositValue) }}</p> <!-- Output: €6,031.56 -->
                    </div>
                </template>
            </Column>
            <Column v-if="isAdmin" field="netRevenue" header="Casino GGR" sortable :headerStyle="headerStyles">
                <template #body="slotProps">
                    <p>{{ formatCurrency(slotProps.data.netRevenue) }}</p>
                </template>
            </Column>
            <!-- <Column field="customerExpectedRevenueAfterCommission" sortable :footer="totalExpectedIncome"
                :headerStyle="headerStyles">
                <template #header>
                    <div class="flex items-center gap-2 p-datatable-column-title">
                        <span>{{ isAdmin ? 'Customer Expected Revenue' : 'Expected Income' }}</span>
                        <i class="pi pi-info-circle text-sm text-gray-500 cursor-help"
                            v-tooltip.bottom="TOOLTIPS.QUALIFIED_USERS_ESTIMATE"></i>
                    </div>
                </template>
                <template #body="slotProps">
                    <div
                        :class="{ 'income-cell': !isAdmin && slotProps.data.customerExpectedRevenueAfterCommission > 0 }">
                        <p>{{ !slotProps.data.customerExpectedRevenueAfterCommission ? '-' :
                            formatCurrency(slotProps.data.customerExpectedRevenueAfterCommission) }}</p>
                    </div>
                </template>
            </Column> -->
            <Column field="customerRevenueAfterCommission" :header="isAdmin ? 'Pub rev' : 'Income'" sortable
                :footer="totalCustomerRevenue" :headerStyle="headerStyles">
                <template #body="{ data }">
                    <div
                        :class="{ 'income-cell': getMetricsCustomerTotal(data) > 0 }">
                        <p>{{ formatCurrency(getMetricsCustomerTotal(data)) }}
                        </p>
                    </div>
                </template>
            </Column>
            <!-- <Column v-if="isAdmin" field="ourExpectedRevenue" header="Our Expected Income" sortable
                :footer="totalOurExpectedIncome" :headerStyle="headerStyles">
                <template #body="slotProps">
                    <div :class="{ 'income-cell': slotProps.data.ourExpectedRevenue > 0 }">
                        <p>{{ !slotProps.data.ourExpectedRevenue ? '-' :
                            formatCurrency(slotProps.data.ourExpectedRevenue) }}</p>
                    </div>
                </template>
            </Column> -->
            <Column v-if="isAdmin" header="Our Income" sortable :footer="totalOurIncome" :headerStyle="headerStyles">

                <template #body="{ data }">
                    <div :class="{ 'income-cell': getMetricsOurTotal(data) > 0 }">
                        <p>{{ formatCurrency(getMetricsOurTotal(data)) }}</p>
                    </div>
                </template>
            </Column>
            <Column v-if="isAdmin" field="totalOperatorRevenue" header="Operator rev" sortable
                :footer="totalOperatorRevenue" :headerStyle="headerStyles">
                <template #body="slotProps">
                    <div :class="{ 'income-cell': slotProps.data.totalOperatorRevenue > 0 }">
                        <p>{{ formatCurrency(slotProps.data.totalOperatorRevenue) }}</p>
                    </div>
                </template>
            </Column>
            <template #expansion="slotProps">
                <div class="expansion-content">
                    <AdvertiserByPublishers v-if="isAdmin && publisherSplit" :advertiser="slotProps.data" :from="dates[0]" :to="dates[1]" />   
                    <AdvertiserDailyStats v-else :advertiser="slotProps.data" :selectedMonth="selectedMonthDate"
                        :key="selectedMonthDate" />
                </div>
            </template>
        </DataTable>
    </div>
</template>

<script setup>
import { headerStyles, getRowStyle } from '@/global/headerStyles';
import { formatCurrency } from '@/utils/currencies';
import { ref, onMounted, reactive, watch, computed, onUnmounted } from "vue";
import { presets, getMonthsForYear, getFirstDayOfCurrentMonth, getLastFourMonths, findCurrentMonthIndex } from '@/utils/dateHelper';
import EarningsService from "@/services/EarningsService";
import AdvertiserDailyStats from '@/components/earnings/AdvertiserDailyStats.vue';
import AdvertiserByPublishers from './AdvertiserByPublishers.vue';
import { useStore } from 'vuex';
import { getMetricsCustomerTotal, getMetricsOurTotal } from '@/utils/earnings';
import DateFilters from './DateFilters.vue';
import FinanceAdvertiserView from './FinanceAdvertiserView.vue';
import { useRouter } from 'vue-router';

const router = useRouter();
const store = useStore();
const user = computed(() => store.state.user);
const isAdmin = computed(() => store.getters.isAdmin);
const isFinance = computed(() => store.getters.isFinance);
const selectedRow = ref(null);
const expandedRows = ref({});
const isExporting = ref(false);
//to render months 
const months = ref(getLastFourMonths());
const selectedMonthIndex = ref(findCurrentMonthIndex(months.value));
const selectedMonthDate = ref(getFirstDayOfCurrentMonth());
const publisherSplit = ref(true);

const updateUrlDates = (from, to) => {
    router.replace({
        query: {
            ...router.currentRoute.value.query,
            from: from.toISOString().split('T')[0],
            to: to.toISOString().split('T')[0]
        }
    });
};

const selectedMonth = (index, from, to) => {
    selectedDatePreset.value = null;
    selectedMonthIndex.value = index;
    dates.value = [from, to];
    selectedMonthDate.value = from;
    updateUrlDates(from, to);
};

const handleMonthSelect = ({ index, start, end }) => {
    selectedDatePreset.value = null;
    selectedMonthIndex.value = index;
    dates.value = [start, end];
    selectedMonthDate.value = start;
    updateUrlDates(start, end);
};
//dates
const datePresets = ref(presets);
const selectedDatePreset = ref(null);
const dates = ref([
    months.value[selectedMonthIndex.value].start,
    months.value[selectedMonthIndex.value].end
]);

const handleDatePresetChange = () => {
    selectedMonthIndex.value = null; // Reset the selected month index
    selectedMonthDate.value = null;
    console.log('Selected date preset changed to:', selectedDatePreset.value);
    dates.value = [selectedDatePreset.value.from, selectedDatePreset.value.to, 'preset'];
};

const loading = ref(true);
//earnings 
const advertiserEarnings = ref([]);
const totalClicks = computed(() => advertiserEarnings.value.reduce((sum, item) => sum + (item.clicks || 0), 0));
const totalUniqueClicks = computed(() => advertiserEarnings.value.reduce((sum, item) => sum + (item.uniqueClicks || 0), 0));
const totalSignups = computed(() => advertiserEarnings.value.reduce((sum, item) => sum + (item.signups || 0), 0));
const totalFTD = computed(() => advertiserEarnings.value.reduce((sum, item) => sum + (item.FTD || 0), 0));
const totalCPAs = computed(() => advertiserEarnings.value.reduce((sum, item) => sum + (item.CPACount || 0), 0));
const totalDeposits = computed(() => advertiserEarnings.value.reduce((sum, item) => sum + (item.deposits || 0), 0));
const totalDepositValue = computed(() => formatCurrency(advertiserEarnings.value.reduce((sum, item) => sum + (item.depositValue || 0), 0)));
const totalCustomerRevenue = computed(() => formatCurrency(advertiserEarnings.value.reduce((sum, item) => sum + getMetricsCustomerTotal(item), 0)));
const totalOurIncome = computed(() => formatCurrency(advertiserEarnings.value.reduce((sum, item) => sum + getMetricsOurTotal(item), 0)));
const totalExpectedIncome = computed(() => formatCurrency(advertiserEarnings.value.reduce((sum, item) => sum + (item.customerExpectedRevenueAfterCommission || 0), 0)));
const totalOurExpectedIncome = computed(() => formatCurrency(advertiserEarnings.value.reduce((sum, item) => sum + (item.ourExpectedRevenue || 0), 0)));
const totalOperatorRevenue = computed(() => formatCurrency(advertiserEarnings.value.reduce((sum, item) => sum + (item.totalOperatorRevenue || 0), 0)));

const defaultSortField = computed(() =>
    isAdmin.value ? 'ourRevenue' : 'customerRevenueAfterCommission'
);

async function GetEarnings(from, to) {
    loading.value = true;
    resetEarnings();
    await EarningsService.getByAdvertiser(from ?? selectedDatePreset.value.from, to ?? selectedDatePreset.value.to)
        .then((response) => {
            advertiserEarnings.value = response.data;
        }).finally(() => loading.value = false);
}

function resetEarnings() {
    advertiserEarnings.value = [];
}

watch(dates, async (newDate) => {
    if (newDate[1] !== null) {
        if (newDate[2] !== 'preset')
            selectedDatePreset.value = null;

        if (newDate[1].getHours() === 0) {
            selectedMonthIndex.value = null;
            selectedMonthDate.value = null;
        }

        await GetEarnings(newDate[0], newDate[1]);
    }
})

onMounted(async () => {
    const { from, to } = router.currentRoute.value.query;
    if (from && to) {
        dates.value = [new Date(from), new Date(to)];

        // Check if dates match any month
        const monthIndex = months.value.findIndex(month => {
            const monthStart = month.start.toISOString().split('T')[0];
            const monthEnd = month.end.toISOString().split('T')[0];
            return from === monthStart && to === monthEnd;
        });

        if (monthIndex !== -1) {
            selectedMonthIndex.value = monthIndex;
        } else {
            selectedMonthIndex.value = null;
        }
    }
    else
        await GetEarnings(
            months.value[selectedMonthIndex.value].start,
            months.value[selectedMonthIndex.value].end
        );
});

const onSelectionChange = (event) => {
    if (!isMobile.value) {
        expandedRows.value = event.value
            ? { [event.value.advertiserId]: true }
            : {};
    }
};

watch(selectedRow, (newValue) => {
    if (newValue) {
        expandedRows.value = { [newValue.advertiserId]: true };
    } else {
        expandedRows.value = {};
    }
});


const canExport = computed(() => {
    return selectedMonthDate.value !== null && !isExporting.value;
});

const isMobile = ref(window.innerWidth <= 768);

const handleResize = () => {
    isMobile.value = window.innerWidth <= 768;
};

onMounted(() => {
    window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
    window.removeEventListener('resize', handleResize);
});

const canNavigateBack = computed(() => selectedMonthIndex.value > 0);
const canNavigateForward = computed(() => selectedMonthIndex.value < months.value.length - 1);
const currentMonthLabel = computed(() => months.value[selectedMonthIndex.value]?.label || '');

const navigateMonth = (direction) => {
    const newIndex = selectedMonthIndex.value + direction;
    if (newIndex >= 0 && newIndex < months.value.length) {
        selectedMonth(newIndex, months.value[newIndex].start, months.value[newIndex].end);
    }
};
</script>

<style scoped>
/* Ensure DatePicker input is full width on mobile */
:deep(.p-calendar) {
    width: 100%;
}

:deep(.p-calendar .p-inputtext) {
    width: 100%;
}

:deep(.p-dropdown) {
    width: 100%;
}

@media (min-width: 640px) {

    :deep(.p-calendar),
    :deep(.p-calendar .p-inputtext) {
        width: auto;
    }

    :deep(.p-dropdown) {
        min-width: 200px;
    }
}

.p-accordionheader {
    padding: 5px !important;
}

.p-accordionpanel {
    border-width: 0 0 0 0;
}

.expansion-content {
    background-color: #1a1a1a;
    padding: 1rem;
    margin: 0.5rem 0;
    border-radius: 0.5rem;
}
</style>